import React from 'react';
import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { FiUpload } from 'react-icons/fi';
import type { Accept } from 'react-dropzone';
import { ff } from '../../../theme';

/** Sheet title + optional context line (board sheet header, below BottomSheet's own close button). */
export const SubmissionSheetTitle: React.FC<{ title: React.ReactNode; context?: React.ReactNode }> = ({ title, context }) => (
  <VStack align="stretch" spacing="8px" mb="16px">
    <Text as="h2" m={0} textStyle="display" fontSize="27px" lineHeight={1.12} letterSpacing="0" color={ff.ink}>
      {title}
    </Text>
    {context && (
      <Text fontSize="14px" fontWeight={600} color="#55555A" lineHeight="normal">
        {context}
      </Text>
    )}
  </VStack>
);

/** Red feedback box. `lead` is the bold opener, e.g. "Cafe Co asked for edits:". */
export const FeedbackBox: React.FC<{ lead: string; children: React.ReactNode }> = ({ lead, children }) => (
  <Box px="14px" py="12px" borderRadius="14px" bg="#FBE1DC" color="#6E1A0A" fontSize="14px" lineHeight={1.4}>
    <Text as="strong" display="block" mb="4px" fontWeight={600}>
      {lead}
    </Text>
    {children}
  </Box>
);

/** Small uppercase section label inside a sheet. */
export const SheetLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text fontSize="12px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color="#55555A" mb="8px">
    {children}
  </Text>
);

export type PillTone = 'dark' | 'red' | 'grey' | 'green';
const PILL_TONES: Record<PillTone, { bg: string; color: string }> = {
  dark: { bg: ff.ink, color: ff.lime },
  red: { bg: '#FBE1DC', color: '#A1260F' },
  grey: { bg: '#F0F0EE', color: '#3E3E42' },
  green: { bg: '#E4F3CC', color: '#2C5208' },
};

export const StatusPill: React.FC<{ tone: PillTone; children: React.ReactNode }> = ({ tone, children }) => (
  <Flex as="span" flex="none" h="28px" px="10px" borderRadius="full" align="center" fontSize="12px" fontWeight={600} whiteSpace="nowrap" {...PILL_TONES[tone]}>
    {children}
  </Flex>
);

/** "MP4, MOV, JPG, PNG or HEIC" from a react-dropzone accept map; formats only, no limits. */
export const acceptedFormatsLabel = (accept: Accept): string => {
  const exts = Array.from(
    new Set(
      Object.values(accept)
        .flat()
        .map(e => e.replace(/^\./, '').toUpperCase())
        .map(e => (e === 'JPEG' ? 'JPG' : e === 'HEIF' ? 'HEIC' : e)),
    ),
  );
  if (exts.length <= 1) return exts.join('');
  return `${exts.slice(0, -1).join(', ')} or ${exts[exts.length - 1]}`;
};

/** Empty dropzone face (board: dashed 132px tile). Spread dropzone root props on the wrapper yourself. */
export const DropzoneFace: React.FC<{ label: string; formats?: string }> = ({ label, formats }) => (
  <VStack spacing="6px" h="132px" justify="center" color={ff.ink}>
    <FiUpload size={26} strokeWidth={2.2} aria-hidden />
    <Text fontSize="15px" fontWeight={600} px={4} maxW="100%" isTruncated>
      {label}
    </Text>
    {formats && (
      <Text fontSize="13px" color="#55555A">
        {formats}
      </Text>
    )}
  </VStack>
);

export const dropzoneBoxProps = {
  borderRadius: '18px',
  border: '1.5px dashed #9A9A9E',
  bg: '#F7F7F5',
  overflow: 'hidden',
  textAlign: 'center' as const,
  cursor: 'pointer',
};

/** Upload progress row: filename + percent over a 6px bar. */
export const UploadProgressRow: React.FC<{ fileName: string; percent: number }> = ({ fileName, percent }) => {
  const pct = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <Box role="status" aria-label={`Uploading ${fileName}, ${pct}%`} px="12px" py="10px" borderRadius="14px" border="1px solid #E4E4E0">
      <Flex justify="space-between" gap="12px" fontSize="14px" mb="6px">
        <Text as="strong" minW={0} isTruncated>
          {fileName}
        </Text>
        <Text as="span" color="#55555A" flex="none">
          {pct}%
        </Text>
      </Flex>
      <Box h="6px" borderRadius="full" bg={ff.line} overflow="hidden">
        <Box h="6px" w={`${pct}%`} bg={ff.ink} transition="width 0.2s" />
      </Box>
    </Box>
  );
};

/** Key/value rows with thin dividers (board viewer). */
export const InfoRows: React.FC<{ rows: { k: string; v: React.ReactNode }[] }> = ({ rows }) => (
  <Box borderTop={`1px solid ${ff.line}`}>
    {rows.map(r => (
      <Flex key={r.k} justify="space-between" gap="12px" py="12px" borderBottom={`1px solid ${ff.line}`} fontSize="14px">
        <Text as="span" color="#55555A">
          {r.k}
        </Text>
        <Text as="strong" textAlign="right" minW={0} wordBreak="break-word">
          {r.v}
        </Text>
      </Flex>
    ))}
  </Box>
);

/** Link input styling (board): 48px, 14px radius, red 2px border on error. */
export const linkInputProps = (invalid: boolean) => ({
  h: '48px',
  px: '14px',
  borderRadius: '14px',
  fontSize: '16px',
  color: ff.ink,
  bg: 'white',
  border: invalid ? '2px solid #A1260F' : `1px solid ${ff.border}`,
  _hover: { borderColor: invalid ? '#A1260F' : ff.border },
  _focusVisible: { borderColor: invalid ? '#A1260F' : ff.ink, boxShadow: 'none' },
  _placeholder: { color: ff.muted },
});

export const FieldError: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text role="alert" fontSize="13px" fontWeight={600} color="#A1260F" mt="6px">
    {children}
  </Text>
);
